import React, { useCallback, useEffect, useRef } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Colors, Theme } from '@app/theme';
import { NavbarColorContext } from './NavbarColorContext';
import { NavbarTandemzLogo } from './NavbarTandemzLogo';
import { NavbarTooltip } from './NavbarTooltip';
import classnames from 'classnames';
import { useLocation } from 'react-router-dom';

const useStyles = createUseStyles<string, { color: Colors }, Theme>(
  (theme: Theme) => ({
    globalContainer: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      background: theme.colors.grayBackground,
      [theme.media.largeScreen]: {
        flexDirection: 'row-reverse',
      },
    },
    pageContent: {
      height: '100%',
      overflow: 'visible',
      position: 'relative',
      [theme.media.largeScreen]: {
        flex: 1,
        overflow: 'auto',
        minHeight: 'none',
      },
    },
    navContentMargin: {
      height: theme.height.navBar,
      position: 'relative',
      bottom: 0,
      [theme.media.largeScreen]: {
        display: 'none',
      },
    },
    navContent: {
      width: '100%',
      position: 'fixed',
      bottom: 0,
      background: theme.colors.inkPrimary,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: [
        theme.marginBase * 2,
        theme.marginBase * 3,
        theme.marginBase * 4,
        theme.marginBase * 3,
      ],
      borderTopLeftRadius: theme.borderRadius.large,
      borderTopRightRadius: theme.borderRadius.large,
      zIndex: 100,
      [theme.media.largeScreen]: {
        position: 'relative',
        flexDirection: 'column',
        width: theme.marginBase * 9,
        borderTopLeftRadius: 0,
        padding: [
          theme.marginBase * 3,
          theme.marginBase * 2,
          theme.marginBase * 3,
          theme.marginBase * 2,
        ],
        borderBottomRightRadius: theme.borderRadius.large,
      },
    },
    nav: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      height: '100%',
      [theme.media.largeScreen]: {
        flexDirection: 'column',
        gap: theme.marginBase,
      },
    },
  }),
);

interface Props {
  children?: React.ReactNode;
  pageContent?: React.ReactNode;
  color?: Colors;
  pageClassName?: string;
  logo?: React.ReactNode;
}

export const Navbar = ({
  children,
  pageContent,
  color = 'red',
  pageClassName,
  logo,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme, color });
  const { pathname } = useLocation();

  const pageContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!pageContentRef.current) {
      return;
    }
    pageContentRef.current.scrollTo(0, 0);
  }, [pageContentRef, pathname]);

  return (
    <NavbarColorContext.Provider value={color}>
      <div className={classnames(classes.globalContainer, pageClassName)}>
        <div className={classes.pageContent} ref={pageContentRef}>
          {pageContent}
          <div className={classes.navContentMargin} />
        </div>
        <div className={classes.navContent}>
          <nav className={classes.nav}>
            {logo ? logo : <NavbarTandemzLogo />}
            {children}
            <NavbarTooltip />
          </nav>
        </div>
      </div>
    </NavbarColorContext.Provider>
  );
};
